import { FetchResult, useMutation } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Mutations, Queries } from '@shared/api';
import { useLocalWorkflowEntityChanges } from '@shared/data/cards/hooks/LocalWorkflowEntityChanges';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { parseApolloError } from '@shared/scenes/entity/util/parseEntityErrors';
import { toFieldJson } from '@shared/scenes/entity/util/toFieldJson';
import { API } from '@shared/types/api';
import { EditableFieldRecord } from '../../types';
import { validateFields } from './useUpdateRecurringEntity';

type UpdateMutationResult = FetchResult<API.UpdateWorkflowEntityMutation>;

interface SaveEntityFieldsHooks {
  loading: boolean;
  errors: CardEntityError[];
  saveEntity: (fields: EditableFieldRecord) => Promise<UpdateMutationResult>;
}

export function useSaveEntityFields(
  entityId: string,
  workflowTemplate?: API.WorkflowTemplateFragment,
  viewTemplateId?: string | null,
): SaveEntityFieldsHooks {
  const [updateEntityMutation, { loading, error }] = useMutation<
    API.UpdateWorkflowEntityMutation,
    API.UpdateWorkflowEntityMutationVariables
  >(Mutations.entity.updateWorkflowEntity);

  const { readCards, publish } = useLocalWorkflowEntityChanges();

  const [errors, setErrors] = useState<CardEntityError[]>([]);

  useEffect(() => {
    if (!isNil(error)) {
      setErrors(parseApolloError(error));
    }
  }, [error]);

  const saveEntity = useCallback(
    (fields: EditableFieldRecord) => {
      const localErrors = validateFields(fields);
      setErrors(localErrors);
      if (!isEmpty(localErrors)) {
        return Promise.reject('Local validation');
      }

      const previousEntities = readCards([entityId]);
      return updateEntityMutation({
        variables: {
          input: {
            id: entityId,
            fields: toFieldJson(fields, workflowTemplate),
            viewTemplateId,
          },
        },
        refetchQueries: [
          { query: Queries.workflow.getWorkflowEntity, variables: { input: { id: entityId } } },
        ],
      }).then(result => {
        const entity = result.data?.updateWorkflowEntity?.entity;
        if (!isNil(entity)) {
          publish({ type: 'WORKFLOW_ENTITY_UPDATED', entities: [entity], previousEntities });
        }
        return result;
      });
    },
    [entityId, updateEntityMutation, workflowTemplate, viewTemplateId, readCards, publish],
  );

  return {
    loading,
    errors,
    saveEntity,
  };
}
